/**
 * API Service for Planned Works Module
 */

import axios from 'axios';
import {
  WorkType,
  PlannedWorkListItem,
  PlannedWorkDetail,
  WorkApproval,
  WorkTodoItem,
  WorkPackage,
  PlannedWorksDashboard,
  CreatePlannedWorkForm,
  CreatePlannedWorkRequest,
  CreateWorkPackageForm,
  CreateWorkPackageRequest,
  ApprovalForm,
  TodoCompletionForm,
  WorkCompletionForm,
  PlannedWorksFilters,
  WorkPackageFilters
} from '../types/plannedWorks';

const API_BASE = '/api/v1/planned-works';

// API Response interfaces
interface APIResponse<T> {
  data: T;
  status: number;
  message?: string;
}

interface PaginatedResponse<T> {
  results: T[];
  count: number;
  next?: string;
  previous?: string;
}

// Work Types API
export const workTypesAPI = {
  async getAll(params?: { 
    category?: string; 
    is_active?: boolean; 
    search?: string; 
    include_inactive?: boolean 
  }): Promise<WorkType[]> {
    const response = await axios.get(`${API_BASE}/work-types/`, { params });
    return response.data.results || response.data;
  },

  async getById(id: number): Promise<WorkType> {
    const response = await axios.get(`${API_BASE}/work-types/${id}/`);
    return response.data;
  },

  async create(data: Partial<WorkType>): Promise<WorkType> {
    const response = await axios.post(`${API_BASE}/work-types/`, data);
    return response.data;
  },

  async update(id: number, data: Partial<WorkType>): Promise<WorkType> {
    const response = await axios.patch(`${API_BASE}/work-types/${id}/`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await axios.delete(`${API_BASE}/work-types/${id}/`);
  },

  async duplicate(id: number): Promise<WorkType> {
    const response = await axios.post(`${API_BASE}/work-types/${id}/duplicate/`);
    return response.data;
  }
};

// Planned Works API
export const plannedWorksAPI = {
  async getAll(params?: PlannedWorksFilters & {
    page?: number;
    page_size?: number;
    ordering?: string;
  }): Promise<PaginatedResponse<PlannedWorkListItem>> {
    const response = await axios.get(`${API_BASE}/works/`, { params });
    return response.data;
  },

  async getById(id: number): Promise<PlannedWorkDetail> {
    const response = await axios.get(`${API_BASE}/works/${id}/`);
    return response.data;
  },

  async create(data: CreatePlannedWorkForm | CreatePlannedWorkRequest): Promise<PlannedWorkDetail> {
    // Convert CreatePlannedWorkRequest to CreatePlannedWorkForm if needed
    let formData: CreatePlannedWorkForm;
    
    if ('work_type_id' in data) {
      // It's a CreatePlannedWorkRequest, convert it
      const requestData = data as CreatePlannedWorkRequest;
      formData = {
        title: requestData.title,
        description: requestData.description,
        site: requestData.site,
        work_type: requestData.work_type_id || 0,
        equipment: requestData.equipment_id ? [requestData.equipment_id] : [],
        location: undefined,
        proposed_start_date: requestData.proposed_start_date ? requestData.proposed_start_date.toISOString().split('T')[0] : '',
        proposed_end_date: requestData.proposed_start_date 
          ? new Date(requestData.proposed_start_date.getTime() + (requestData.estimated_duration_hours * 60 * 60 * 1000)).toISOString().split('T')[0] 
          : '',
        estimated_duration_hours: requestData.estimated_duration_hours,
        priority: requestData.priority === 'critical' ? 'emergency' : requestData.priority as 'low' | 'normal' | 'high' | 'urgent' | 'emergency',
        assigned_to: requestData.assigned_to_id,
        permit_required: requestData.requires_permit,
        isolation_required: requestData.requires_isolation,
        confined_space_entry: requestData.confined_space,
        hot_work: requestData.hot_work,
        required_crew_size: 1,
        required_skills: '',
        required_tools: '',
        required_parts: '',
        special_instructions: requestData.special_instructions,
        safety_requirements: [
          requestData.requires_ppe ? 'PPE Required' : '',
          requestData.work_at_height ? 'Work at Height' : '',
          requestData.requires_isolation ? 'Isolation Required' : ''
        ].filter(Boolean).join(', '),
        prerequisite_works: [],
        related_maintenance_tasks: []
      };
    } else {
      // It's already a CreatePlannedWorkForm
      formData = data as CreatePlannedWorkForm;
    }
    
    const response = await axios.post(`${API_BASE}/works/`, formData);
    return response.data;
  },

  async update(id: number, data: Partial<CreatePlannedWorkForm>): Promise<PlannedWorkDetail> {
    const response = await axios.patch(`${API_BASE}/works/${id}/`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await axios.delete(`${API_BASE}/works/${id}/`);
  },

  async submitForApproval(id: number): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/submit_for_approval/`);
    return response.data;
  },

  async schedule(id: number, data: {
    scheduled_start_date?: string;
    scheduled_end_date?: string;
    notes?: string;
  }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/schedule/`, data);
    return response.data;
  },

  async startExecution(id: number): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/start_execution/`);
    return response.data;
  },

  async complete(id: number, data: WorkCompletionForm): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/complete/`, data);
    return response.data;
  },

  async canStartCheck(id: number): Promise<{
    can_start: boolean;
    checks: Record<string, boolean>;
    prerequisite_status: any;
  }> {
    const response = await axios.get(`${API_BASE}/works/${id}/can_start_check/`);
    return response.data;
  },

  async getOverdue(): Promise<PlannedWorkListItem[]> {
    const response = await axios.get(`${API_BASE}/works/overdue/`);
    return response.data;
  },

  async getPendingApproval(): Promise<PlannedWorkListItem[]> {
    const response = await axios.get(`${API_BASE}/works/pending_approval/`);
    return response.data;
  },

  // Progress tracking methods
  async propose(id: number, data: { notes: string }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/propose/`, data);
    return response.data;
  },

  async approve(id: number, data: { notes: string }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/approve/`, data);
    return response.data;
  },

  async reject(id: number, data: { notes: string }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/reject/`, data);
    return response.data;
  },

  async startWork(id: number, data: { notes: string }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/start_execution/`, data);
    return response.data;
  },

  async pauseWork(id: number, data: { notes: string }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/pause/`, data);
    return response.data;
  },

  async resumeWork(id: number, data: { notes: string }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/resume/`, data);
    return response.data;
  },

  async completeWork(id: number, data: { notes: string; completion_percentage?: number }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/complete/`, data);
    return response.data;
  },

  async cancelWork(id: number, data: { notes: string }): Promise<PlannedWorkDetail> {
    const response = await axios.post(`${API_BASE}/works/${id}/cancel/`, data);
    return response.data;
  },

  async getProgressHistory(id: number): Promise<any[]> {
    const response = await axios.get(`${API_BASE}/works/${id}/progress_history/`);
    return response.data;
  },

  async addComment(id: number, data: { comment: string }): Promise<void> {
    await axios.post(`${API_BASE}/works/${id}/add_comment/`, data);
  }
};

// Work Approvals API
export const workApprovalsAPI = {
  async getAll(params?: {
    approval_stage?: string;
    status?: string;
    planned_work?: number;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<WorkApproval>> {
    const response = await axios.get(`${API_BASE}/work-approvals/`, { params });
    return response.data;
  },

  async getById(id: number): Promise<WorkApproval> {
    const response = await axios.get(`${API_BASE}/work-approvals/${id}/`);
    return response.data;
  },

  async create(data: Partial<WorkApproval>): Promise<WorkApproval> {
    const response = await axios.post(`${API_BASE}/work-approvals/`, data);
    return response.data;
  },

  async update(id: number, data: Partial<WorkApproval>): Promise<WorkApproval> {
    const response = await axios.patch(`${API_BASE}/work-approvals/${id}/`, data);
    return response.data;
  },

  async approve(workId: number, data: ApprovalForm): Promise<{
    message: string;
    approval: WorkApproval;
    work_status: string;
  }> {
    const response = await axios.post(`${API_BASE}/works/${workId}/approve/`, data);
    return response.data;
  }
};

// Todo Items API
export const todoItemsAPI = {
  async getAll(params?: {
    planned_work?: number;
    status?: string;
    assigned_to?: number;
    page?: number;
    page_size?: number;
  }): Promise<PaginatedResponse<WorkTodoItem>> {
    const response = await axios.get(`${API_BASE}/todo-items/`, { params });
    return response.data;
  },

  async getById(id: number): Promise<WorkTodoItem> {
    const response = await axios.get(`${API_BASE}/todo-items/${id}/`);
    return response.data;
  },

  async create(data: Partial<WorkTodoItem>): Promise<WorkTodoItem> {
    const response = await axios.post(`${API_BASE}/todo-items/`, data);
    return response.data;
  },

  async update(id: number, data: Partial<WorkTodoItem>): Promise<WorkTodoItem> {
    const response = await axios.patch(`${API_BASE}/todo-items/${id}/`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await axios.delete(`${API_BASE}/todo-items/${id}/`);
  },

  async complete(id: number, data: TodoCompletionForm): Promise<WorkTodoItem> {
    const response = await axios.post(`${API_BASE}/todo-items/${id}/complete/`, data);
    return response.data;
  },

  async block(id: number, blocked_reason: string): Promise<WorkTodoItem> {
    const response = await axios.post(`${API_BASE}/todo-items/${id}/block/`, { blocked_reason });
    return response.data;
  }
};

// Work Packages API
export const workPackagesAPI = {
  async getAll(params?: WorkPackageFilters & {
    page?: number;
    page_size?: number;
    ordering?: string;
  }): Promise<PaginatedResponse<WorkPackage>> {
    const response = await axios.get(`${API_BASE}/work-packages/`, { params });
    return response.data;
  },

  async getById(id: number): Promise<WorkPackage> {
    const response = await axios.get(`${API_BASE}/work-packages/${id}/`);
    return response.data;
  },

  async create(data: CreateWorkPackageForm | CreateWorkPackageRequest): Promise<WorkPackage> {
    // Convert CreateWorkPackageRequest to CreateWorkPackageForm if needed
    let formData: CreateWorkPackageForm;
    
    if ('planned_start_date' in data && data.planned_start_date instanceof Date) {
      // It's a CreateWorkPackageRequest, convert it
      const requestData = data as CreateWorkPackageRequest;
      formData = {
        package_name: requestData.name,
        package_description: requestData.description,
        site: requestData.site,
        planned_works: requestData.works,
        scheduled_start_date: requestData.planned_start_date ? requestData.planned_start_date.toISOString().split('T')[0] : '',
        scheduled_end_date: requestData.planned_completion_date ? requestData.planned_completion_date.toISOString().split('T')[0] : '',
        package_coordinator: requestData.coordinator_id,
        assigned_crew: [],
        total_estimated_hours: 0,
        required_crew_size: requestData.max_concurrent_works,
        priority: 'normal',
        coordination_notes: requestData.special_instructions,
        created_by: 0
      };
    } else {
      // It's already a CreateWorkPackageForm
      formData = data as CreateWorkPackageForm;
    }
    
    const response = await axios.post(`${API_BASE}/work-packages/`, formData);
    return response.data;
  },

  async get(id: number): Promise<WorkPackage> {
    const response = await axios.get(`${API_BASE}/work-packages/${id}/`);
    return response.data;
  },

  async update(id: number, data: Partial<CreateWorkPackageForm>): Promise<WorkPackage> {
    const response = await axios.patch(`${API_BASE}/work-packages/${id}/`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await axios.delete(`${API_BASE}/work-packages/${id}/`);
  },

  async addWork(id: number, workId: number): Promise<WorkPackage> {
    const response = await axios.post(`${API_BASE}/work-packages/${id}/add_work/`, { work_id: workId });
    return response.data;
  },

  async removeWork(id: number, workId: number): Promise<WorkPackage> {
    const response = await axios.post(`${API_BASE}/work-packages/${id}/remove_work/`, { work_id: workId });
    return response.data;
  },

  async calculateTotals(id: number): Promise<{
    package_totals: any;
    total_estimated_hours: number;
    required_crew_size: number;
  }> {
    const response = await axios.post(`${API_BASE}/work-packages/${id}/calculate_totals/`);
    return response.data;
  }
};

// Dashboard API
export const dashboardAPI = {
  async getDashboard(siteId: number): Promise<PlannedWorksDashboard> {
    const response = await axios.get(`${API_BASE}/dashboard/${siteId}/`);
    return response.data;
  }
};

// Permit Integration API
export const permitIntegrationAPI = {
  async generatePermit(workId: number): Promise<{
    message: string;
    work_id: number;
  }> {
    const response = await axios.post(`${API_BASE}/works/${workId}/generate-permit/`);
    return response.data;
  }
};

// Utility functions
export const plannedWorksUtils = {
  /**
   * Format duration hours to human readable format
   */
  formatDuration(hours: number): string {
    if (hours < 1) {
      return `${Math.round(hours * 60)} min`;
    } else if (hours < 24) {
      return `${hours} hrs`;
    } else {
      const days = Math.floor(hours / 24);
      const remainingHours = hours % 24;
      return remainingHours > 0 ? `${days}d ${remainingHours}h` : `${days} days`;
    }
  },

  /**
   * Calculate days between two dates
   */
  daysBetween(startDate: string, endDate: string): number {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  },

  /**
   * Check if a date is in the past
   */
  isOverdue(date: string): boolean {
    const today = new Date();
    const targetDate = new Date(date);
    return targetDate < today;
  },

  /**
   * Format progress percentage
   */
  formatProgress(completed: number, total: number): string {
    if (total === 0) return '0%';
    const percentage = Math.round((completed / total) * 100);
    return `${percentage}%`;
  },

  /**
   * Get next available sequence order for todo items
   */
  getNextTodoSequence(todoItems: WorkTodoItem[]): number {
    if (!todoItems || todoItems.length === 0) return 1;
    const maxSequence = Math.max(...todoItems.map(item => item.sequence_order));
    return maxSequence + 1;
  },

  /**
   * Check if all prerequisites are met
   */
  canStartWork(work: PlannedWorkDetail): boolean {
    return work.can_start_execution.can_start;
  },

  /**
   * Get work status color for UI display
   */
  getStatusColor(status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    const statusColors: Record<string, any> = {
      'draft': 'default',
      'proposed': 'info',
      'under_review': 'warning',
      'approved': 'success',
      'scheduled': 'primary',
      'ready_to_execute': 'success',
      'in_progress': 'primary',
      'on_hold': 'warning',
      'completed': 'success',
      'cancelled': 'error'
    };
    return statusColors[status] || 'default';
  },

  /**
   * Get priority color for UI display
   */
  getPriorityColor(priority: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' {
    const priorityColors: Record<string, any> = {
      'low': 'default',
      'normal': 'info',
      'high': 'warning',
      'urgent': 'error',
      'emergency': 'error'
    };
    return priorityColors[priority] || 'default';
  }
};

// Export default service object
const plannedWorksService = {
  workTypes: workTypesAPI,
  plannedWorks: plannedWorksAPI,
  approvals: workApprovalsAPI,
  todoItems: todoItemsAPI,
  workTodoItems: todoItemsAPI, // Alias for compatibility
  workPackages: workPackagesAPI,
  dashboard: dashboardAPI,
  permitIntegration: permitIntegrationAPI,
  utils: plannedWorksUtils
};

export default plannedWorksService;
